// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * A small WebGL2 renderer, written directly against the API rather than pulled
 * in from three.js.
 *
 * The viewport needs exactly three things: flat-shaded triangles, coloured line
 * lists, and a grid. A general-purpose scene graph would be an order of
 * magnitude more code shipped to the user for no gain — and the one feature
 * that matters most here, drawing the broken edges in red over the surface, is
 * a second draw call rather than a material system.
 *
 * Two deliberate choices, both because the models this tool loads are the
 * broken ones:
 *
 *  - **No normal buffer.** The face normal is recovered in the fragment shader
 *    from the screen-space derivatives of the world position. That is exactly
 *    flat shading, which is what a faceted print mesh should look like, and it
 *    halves the geometry uploaded to the GPU.
 *  - **No backface culling, two-sided lighting.** A mesh with inverted normals
 *    would otherwise render inside-out or invisible — and telling the user their
 *    normals are inverted is part of the job, so the model has to stay visible.
 */

const MESH_VS = `#version 300 es
in vec3 aPos;
uniform mat4 uModel;
uniform mat4 uViewProj;
out vec3 vWorld;
void main() {
  vec4 world = uModel * vec4(aPos, 1.0);
  vWorld = world.xyz;
  gl_Position = uViewProj * world;
}`;

const MESH_FS = `#version 300 es
precision highp float;
in vec3 vWorld;
uniform vec3 uCam;
uniform vec3 uColor;
out vec4 fragColor;
void main() {
  vec3 n = normalize(cross(dFdx(vWorld), dFdy(vWorld)));
  vec3 v = normalize(uCam - vWorld);
  if (dot(n, v) < 0.0) n = -n;
  vec3 key = normalize(vec3(0.45, 0.55, 0.85));
  vec3 fill = normalize(vec3(-0.7, -0.35, 0.3));
  float lambert = max(dot(n, key), 0.0) * 0.82 + max(dot(n, fill), 0.0) * 0.26;
  float rim = pow(1.0 - max(dot(n, v), 0.0), 2.5) * 0.4;
  vec3 c = uColor * (0.2 + lambert) + vec3(0.98, 0.66, 0.30) * rim;
  fragColor = vec4(c, 1.0);
}`;

const LINE_VS = `#version 300 es
in vec3 aPos;
uniform mat4 uModel;
uniform mat4 uViewProj;
uniform float uBias;
void main() {
  gl_Position = uViewProj * uModel * vec4(aPos, 1.0);
  // Nudge toward the camera in clip space so an edge lying exactly on the
  // surface it belongs to does not z-fight with it.
  gl_Position.z -= uBias * gl_Position.w;
}`;

const LINE_FS = `#version 300 es
precision highp float;
uniform vec4 uColor;
out vec4 fragColor;
void main() { fragColor = uColor; }`;

/** Wireframe triples the line data; past this it is a grey blur anyway. */
const MAX_WIREFRAME_TRIANGLES = 400_000;

export interface ViewerOptions {
  wireframe: boolean;
  showProblemEdges: boolean;
}

/* ────────────────────────────── mat4 helpers ────────────────────────── */

type Mat4 = Float32Array;

function identity(): Mat4 {
  const m = new Float32Array(16);
  m[0] = m[5] = m[10] = m[15] = 1;
  return m;
}

function perspective(fovY: number, aspect: number, near: number, far: number): Mat4 {
  const f = 1 / Math.tan(fovY / 2);
  const m = new Float32Array(16);
  m[0] = f / aspect;
  m[5] = f;
  m[10] = (far + near) / (near - far);
  m[11] = -1;
  m[14] = (2 * far * near) / (near - far);
  return m;
}

function lookAt(eye: number[], target: number[], up: number[]): Mat4 {
  let zx = eye[0] - target[0];
  let zy = eye[1] - target[1];
  let zz = eye[2] - target[2];
  let len = Math.hypot(zx, zy, zz) || 1;
  zx /= len;
  zy /= len;
  zz /= len;

  let xx = up[1] * zz - up[2] * zy;
  let xy = up[2] * zx - up[0] * zz;
  let xz = up[0] * zy - up[1] * zx;
  len = Math.hypot(xx, xy, xz) || 1;
  xx /= len;
  xy /= len;
  xz /= len;

  const yx = zy * xz - zz * xy;
  const yy = zz * xx - zx * xz;
  const yz = zx * xy - zy * xx;

  const m = new Float32Array(16);
  m[0] = xx;
  m[1] = yx;
  m[2] = zx;
  m[4] = xy;
  m[5] = yy;
  m[6] = zy;
  m[8] = xz;
  m[9] = yz;
  m[10] = zz;
  m[12] = -(xx * eye[0] + xy * eye[1] + xz * eye[2]);
  m[13] = -(yx * eye[0] + yy * eye[1] + yz * eye[2]);
  m[14] = -(zx * eye[0] + zy * eye[1] + zz * eye[2]);
  m[15] = 1;
  return m;
}

function multiply(a: Mat4, b: Mat4): Mat4 {
  const out = new Float32Array(16);
  for (let c = 0; c < 4; c++) {
    for (let r = 0; r < 4; r++) {
      out[c * 4 + r] =
        a[r] * b[c * 4] + a[4 + r] * b[c * 4 + 1] + a[8 + r] * b[c * 4 + 2] + a[12 + r] * b[c * 4 + 3];
    }
  }
  return out;
}

function translation(x: number, y: number, z: number): Mat4 {
  const m = identity();
  m[12] = x;
  m[13] = y;
  m[14] = z;
  return m;
}

/* ─────────────────────────── frame scheduling ───────────────────────── */

/**
 * requestAnimationFrame never fires in a background or headless tab, which
 * would leave the canvas permanently blank there. Rendering is on-demand
 * anyway (there is no animation), so a timeout races the rAF and whichever
 * arrives first draws the frame.
 */
function onNextFrame(callback: () => void): void {
  let done = false;
  const run = () => {
    if (done) return;
    done = true;
    callback();
  };
  const id = requestAnimationFrame(run);
  setTimeout(() => {
    if (!done) cancelAnimationFrame(id);
    run();
  }, 100);
}

/* ──────────────────────────────── viewer ────────────────────────────── */

export class Viewer {
  private gl: WebGL2RenderingContext;
  private canvas: HTMLCanvasElement;
  private meshProgram: WebGLProgram;
  private lineProgram: WebGLProgram;

  private meshVao: WebGLVertexArrayObject | null = null;
  private meshCount = 0;
  private problemVao: WebGLVertexArrayObject | null = null;
  private problemCount = 0;
  private wireVao: WebGLVertexArrayObject | null = null;
  private wireCount = 0;
  private gridVao: WebGLVertexArrayObject | null = null;
  private gridCount = 0;
  private buffers: WebGLBuffer[] = [];

  private modelMatrix: Mat4 = identity();
  private yaw = -0.9;
  private pitch = 0.55;
  private distance = 200;
  private target: [number, number, number] = [0, 0, 0];
  private radius = 100;

  private options: ViewerOptions = { wireframe: false, showProblemEdges: true };
  private dirty = false;
  private pointers = new Map<number, { x: number; y: number }>();
  private lastPinch = 0;
  private disposed = false;

  static create(canvas: HTMLCanvasElement): Viewer | null {
    const gl = canvas.getContext('webgl2', {
      antialias: true,
      alpha: false,
      preserveDrawingBuffer: false,
      powerPreference: 'high-performance',
    });
    if (!gl) return null;
    return new Viewer(canvas, gl);
  }

  private constructor(canvas: HTMLCanvasElement, gl: WebGL2RenderingContext) {
    this.canvas = canvas;
    this.gl = gl;
    this.meshProgram = this.link(MESH_VS, MESH_FS);
    this.lineProgram = this.link(LINE_VS, LINE_FS);

    gl.enable(gl.DEPTH_TEST);
    gl.disable(gl.CULL_FACE);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.clearColor(0.043, 0.051, 0.067, 1);

    this.attachControls();
  }

  private link(vsSource: string, fsSource: string): WebGLProgram {
    const gl = this.gl;
    const compile = (type: number, source: string): WebGLShader => {
      const shader = gl.createShader(type);
      if (!shader) throw new Error('Could not create a WebGL shader.');
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        const log = gl.getShaderInfoLog(shader) ?? 'unknown error';
        gl.deleteShader(shader);
        throw new Error(`Shader failed to compile: ${log}`);
      }
      return shader;
    };

    const vs = compile(gl.VERTEX_SHADER, vsSource);
    const fs = compile(gl.FRAGMENT_SHADER, fsSource);
    const program = gl.createProgram();
    if (!program) throw new Error('Could not create a WebGL program.');
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.bindAttribLocation(program, 0, 'aPos');
    gl.linkProgram(program);
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      throw new Error(`WebGL program failed to link: ${gl.getProgramInfoLog(program) ?? ''}`);
    }
    return program;
  }

  private makeVao(data: Float32Array): { vao: WebGLVertexArrayObject; count: number } {
    const gl = this.gl;
    const vao = gl.createVertexArray();
    const buffer = gl.createBuffer();
    if (!vao || !buffer) throw new Error('Could not allocate GPU buffers.');
    gl.bindVertexArray(vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);
    this.buffers.push(buffer);
    return { vao, count: data.length / 3 };
  }

  private clearGeometry(): void {
    const gl = this.gl;
    for (const vao of [this.meshVao, this.problemVao, this.wireVao, this.gridVao]) {
      if (vao) gl.deleteVertexArray(vao);
    }
    for (const buffer of this.buffers) gl.deleteBuffer(buffer);
    this.buffers = [];
    this.meshVao = this.problemVao = this.wireVao = this.gridVao = null;
    this.meshCount = this.problemCount = this.wireCount = this.gridCount = 0;
  }

  /**
   * Upload a mesh. `positions` is the raw soup; `problemLines` is a line list of
   * open and non-manifold edges in the same coordinate space.
   */
  setMesh(positions: Float32Array, problemLines: Float32Array): void {
    if (this.disposed) return;
    this.clearGeometry();

    let minX = Infinity;
    let minY = Infinity;
    let minZ = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let maxZ = -Infinity;
    for (let i = 0; i < positions.length; i += 3) {
      if (positions[i] < minX) minX = positions[i];
      if (positions[i + 1] < minY) minY = positions[i + 1];
      if (positions[i + 2] < minZ) minZ = positions[i + 2];
      if (positions[i] > maxX) maxX = positions[i];
      if (positions[i + 1] > maxY) maxY = positions[i + 1];
      if (positions[i + 2] > maxZ) maxZ = positions[i + 2];
    }

    const sizeX = maxX - minX;
    const sizeY = maxY - minY;
    const sizeZ = maxZ - minZ;

    // The model is presented sitting on the plate and centred, which is how a
    // slicer shows it; the file's own origin is rarely meaningful.
    this.modelMatrix = translation(-(minX + maxX) / 2, -(minY + maxY) / 2, -minZ);
    this.radius = Math.max(Math.hypot(sizeX, sizeY, sizeZ) / 2, 1e-3);
    this.target = [0, 0, sizeZ / 2];

    const mesh = this.makeVao(positions);
    this.meshVao = mesh.vao;
    this.meshCount = mesh.count;

    if (problemLines.length >= 6) {
      const problem = this.makeVao(problemLines);
      this.problemVao = problem.vao;
      this.problemCount = problem.count;
    }

    const triangles = positions.length / 9;
    if (triangles > 0 && triangles <= MAX_WIREFRAME_TRIANGLES) {
      const wire = new Float32Array(triangles * 18);
      let w = 0;
      for (let i = 0; i < positions.length; i += 9) {
        for (const [a, b] of [
          [0, 3],
          [3, 6],
          [6, 0],
        ]) {
          wire[w++] = positions[i + a];
          wire[w++] = positions[i + a + 1];
          wire[w++] = positions[i + a + 2];
          wire[w++] = positions[i + b];
          wire[w++] = positions[i + b + 1];
          wire[w++] = positions[i + b + 2];
        }
      }
      const wv = this.makeVao(wire);
      this.wireVao = wv.vao;
      this.wireCount = wv.count;
    }

    this.buildGrid(Math.max(sizeX, sizeY));
    this.resetView();
  }

  /** Wireframe availability depends on size; the UI greys the button out. */
  get wireframeAvailable(): boolean {
    return this.wireVao !== null;
  }

  private buildGrid(span: number): void {
    const step = niceStep(span);
    const half = Math.ceil((span * 0.9) / step) * step + step;
    const lines: number[] = [];
    for (let v = -half; v <= half + 1e-6; v += step) {
      lines.push(-half, v, 0, half, v, 0);
      lines.push(v, -half, 0, v, half, 0);
    }
    const grid = this.makeVao(Float32Array.from(lines));
    this.gridVao = grid.vao;
    this.gridCount = grid.count;
  }

  setOptions(options: Partial<ViewerOptions>): void {
    this.options = { ...this.options, ...options };
    this.requestRender();
  }

  resetView(): void {
    this.yaw = -0.9;
    this.pitch = 0.55;
    // Frame the bounding sphere in the vertical field of view, with headroom.
    this.distance = (this.radius / Math.sin(Math.PI / 8)) * 1.15;
    this.requestRender();
  }

  requestRender(): void {
    if (this.dirty || this.disposed) return;
    this.dirty = true;
    onNextFrame(() => {
      this.dirty = false;
      this.render();
    });
  }

  /**
   * Match the drawing buffer to the CSS box, or report that there is no box yet.
   *
   * The `false` return is the important case. A canvas in a hidden tab, a
   * display:none ancestor, or a pane that has not been laid out yet measures
   * 0×0. Clamping that to 1×1 and drawing anyway produces a one-pixel image
   * that the browser then stretches across the full element the moment it does
   * become visible — which looks exactly like a camera stuck at extreme zoom.
   * Far better to draw nothing and wait: the ResizeObserver fires as soon as a
   * real size exists and asks for the frame again.
   */
  private resize(): boolean {
    const cssWidth = this.canvas.clientWidth;
    const cssHeight = this.canvas.clientHeight;
    if (cssWidth <= 0 || cssHeight <= 0) return false;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const width = Math.max(1, Math.round(cssWidth * dpr));
    const height = Math.max(1, Math.round(cssHeight * dpr));
    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
    }
    return true;
  }

  render(): void {
    if (this.disposed) return;
    const gl = this.gl;
    if (!this.resize()) return;
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    if (!this.meshVao) return;

    const aspect = this.canvas.width / Math.max(1, this.canvas.height);
    const cosPitch = Math.cos(this.pitch);
    const eye: [number, number, number] = [
      this.target[0] + this.distance * cosPitch * Math.cos(this.yaw),
      this.target[1] + this.distance * cosPitch * Math.sin(this.yaw),
      this.target[2] + this.distance * Math.sin(this.pitch),
    ];

    const near = Math.max(this.distance * 0.005, 1e-4);
    const far = this.distance + this.radius * 8 + 1;
    const viewProj = multiply(
      perspective(Math.PI / 4, aspect, near, far),
      lookAt(eye, this.target, [0, 0, 1]),
    );

    // Plate grid first — it is behind everything and needs no depth writes.
    if (this.gridVao) {
      gl.useProgram(this.lineProgram);
      this.setLineUniforms(viewProj, identity(), [0.36, 0.4, 0.48, 0.32], 0);
      gl.bindVertexArray(this.gridVao);
      gl.drawArrays(gl.LINES, 0, this.gridCount);
    }

    gl.useProgram(this.meshProgram);
    gl.uniformMatrix4fv(gl.getUniformLocation(this.meshProgram, 'uViewProj'), false, viewProj);
    gl.uniformMatrix4fv(gl.getUniformLocation(this.meshProgram, 'uModel'), false, this.modelMatrix);
    gl.uniform3f(gl.getUniformLocation(this.meshProgram, 'uCam'), eye[0], eye[1], eye[2]);
    gl.uniform3f(gl.getUniformLocation(this.meshProgram, 'uColor'), 0.56, 0.6, 0.68);
    gl.bindVertexArray(this.meshVao);
    gl.drawArrays(gl.TRIANGLES, 0, this.meshCount);

    gl.useProgram(this.lineProgram);
    if (this.options.wireframe && this.wireVao) {
      this.setLineUniforms(viewProj, this.modelMatrix, [0.05, 0.06, 0.08, 0.38], 0.0006);
      gl.bindVertexArray(this.wireVao);
      gl.drawArrays(gl.LINES, 0, this.wireCount);
    }

    if (this.options.showProblemEdges && this.problemVao) {
      this.setLineUniforms(viewProj, this.modelMatrix, [1, 0.27, 0.27, 1], 0.0015);
      gl.bindVertexArray(this.problemVao);
      gl.drawArrays(gl.LINES, 0, this.problemCount);
    }

    gl.bindVertexArray(null);
  }

  private setLineUniforms(
    viewProj: Mat4,
    model: Mat4,
    color: [number, number, number, number],
    bias: number,
  ): void {
    const gl = this.gl;
    gl.uniformMatrix4fv(gl.getUniformLocation(this.lineProgram, 'uViewProj'), false, viewProj);
    gl.uniformMatrix4fv(gl.getUniformLocation(this.lineProgram, 'uModel'), false, model);
    gl.uniform4f(gl.getUniformLocation(this.lineProgram, 'uColor'), ...color);
    gl.uniform1f(gl.getUniformLocation(this.lineProgram, 'uBias'), bias);
  }

  /* ───────────────────────────── controls ──────────────────────────── */

  private attachControls(): void {
    const canvas = this.canvas;

    canvas.addEventListener('contextmenu', (e) => e.preventDefault());

    canvas.addEventListener('pointerdown', (e) => {
      canvas.setPointerCapture(e.pointerId);
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      this.lastPinch = 0;
    });

    const release = (e: PointerEvent) => {
      this.pointers.delete(e.pointerId);
      this.lastPinch = 0;
    };
    canvas.addEventListener('pointerup', release);
    canvas.addEventListener('pointercancel', release);

    canvas.addEventListener('pointermove', (e) => {
      const previous = this.pointers.get(e.pointerId);
      if (!previous) return;
      const dx = e.clientX - previous.x;
      const dy = e.clientY - previous.y;
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (this.pointers.size >= 2) {
        const points = [...this.pointers.values()];
        const spread = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
        if (this.lastPinch > 0 && spread > 0) {
          this.zoom(this.lastPinch / spread);
        }
        this.lastPinch = spread;
        this.pan(dx / 2, dy / 2);
        return;
      }

      // Right button, middle button, or a modifier: pan. Otherwise orbit.
      if (e.buttons & 2 || e.buttons & 4 || e.shiftKey) this.pan(dx, dy);
      else this.orbit(dx, dy);
    });

    canvas.addEventListener(
      'wheel',
      (e) => {
        e.preventDefault();
        this.zoom(Math.exp(e.deltaY * 0.0015));
      },
      { passive: false },
    );

    window.addEventListener('resize', () => this.requestRender());
    // A tab restored from the background may never have had a measurable
    // canvas, so the first real frame is owed as soon as it comes forward.
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) this.requestRender();
    });
  }

  private orbit(dx: number, dy: number): void {
    this.yaw -= dx * 0.008;
    this.pitch = clamp(this.pitch + dy * 0.008, -1.5, 1.5);
    this.requestRender();
  }

  private zoom(factor: number): void {
    this.distance = clamp(this.distance * factor, this.radius * 0.05, this.radius * 60);
    this.requestRender();
  }

  private pan(dx: number, dy: number): void {
    // Move the target across the camera's own screen plane, scaled so a drag
    // tracks the cursor at roughly 1:1 regardless of zoom level.
    const scale = (this.distance * 0.0022) / Math.max(this.canvas.clientHeight / 600, 0.3);
    const right: [number, number, number] = [-Math.sin(this.yaw), Math.cos(this.yaw), 0];
    const sinPitch = Math.sin(this.pitch);
    const up: [number, number, number] = [
      -Math.cos(this.yaw) * sinPitch,
      -Math.sin(this.yaw) * sinPitch,
      Math.cos(this.pitch),
    ];
    for (let a = 0; a < 3; a++) {
      this.target[a] += (-right[a] * dx + up[a] * dy) * scale;
    }
    this.requestRender();
  }

  dispose(): void {
    this.disposed = true;
    this.clearGeometry();
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** Grid spacing that lands on 1/2/5 × a power of ten, like a ruler. */
export function niceStep(span: number): number {
  const raw = Math.max(span, 1) / 10;
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
  const normalised = raw / magnitude;
  const step = normalised >= 5 ? 5 : normalised >= 2 ? 2 : 1;
  return step * magnitude;
}
